import {

    useState

}

from "react";


//========================================================
// Snippet Document Builder
//
// Owns the working document layout.
//
// Document
//     Page
//         Row
//             Cell
//
// Cell content can now contain:
//
// - Basket references
// - Authored text
// - Edited copies of source text
// - Rich-text HTML and plain-text snapshots
// - Image captions and formatting
//
// Original basket content remains untouched.
//
// Uploaded image Files require a separate upload workflow
// before the document can be persisted as JSON.
//========================================================

export default function useSnippetDocumentBuilder()

{

    //========================================================
    // Create ID
    //========================================================

    const createId = (prefix) =>

    {

        if (

            typeof crypto !== "undefined" &&

            typeof crypto.randomUUID === "function"

        )

        {

            return (

                prefix +

                "-" +

                crypto.randomUUID()

            );

        }


        return (

            prefix +

            "-" +

            Date.now() +

            "-" +

            Math.random()

                .toString(36)

                .substring(

                    2,

                    10

                )

        );

    };


    //========================================================
    // Create Cell
    //========================================================

    const createCell = (width = 100) =>

    {

        return {

            id:

                createId(

                    "cell"

                ),

            width:

                width,

            content:

                null

        };

    };


    //========================================================
    // Create Row
    //========================================================

    const createRow = (

        widths = [100]

    ) =>

    {

        return {

            id:

                createId(

                    "row"

                ),

            cells:

                widths.map(

                    width =>

                        createCell(

                            width

                        )

                )

        };

    };


    //========================================================
    // Create Page
    //========================================================

    const createPage = () =>

    {

        return {

            id:

                createId(

                    "page"

                ),

            orientation:

                "PORTRAIT",

            rows:

                []

        };

    };


    //========================================================
    // Document
    //========================================================

    const [

        documentLayout,

        setDocumentLayout

    ] =

        useState(

            () =>

            ({

                id:

                    createId(

                        "document"

                    ),

                title:

                    "Untitled Document",

                pages:

                    [

                        createPage()

                    ]

            })

        );


    //========================================================
    // Update Cell
    //
    // Shared immutable update for cell operations.
    //
    // updater receives the existing cell and returns the
    // replacement cell.
    //========================================================

    const updateCell = (

        pageId,

        rowId,

        cellId,

        updater

    ) =>

    {

        setDocumentLayout(

            current =>

            ({

                ...current,

                pages:

                    current.pages.map(

                        page =>

                        {

                            if (

                                page.id !== pageId

                            )

                            {

                                return page;

                            }


                            return {

                                ...page,

                                rows:

                                    page.rows.map(

                                        row =>

                                        {

                                            if (

                                                row.id !== rowId

                                            )

                                            {

                                                return row;

                                            }


                                            return {

                                                ...row,

                                                cells:

                                                    row.cells.map(

                                                        cell =>

                                                        {

                                                            if (

                                                                cell.id !== cellId

                                                            )

                                                            {

                                                                return cell;

                                                            }


                                                            return updater(

                                                                cell

                                                            );

                                                        }

                                                    )

                                            };

                                        }

                                    )

                            };

                        }

                    )

            })

        );

    };


    //========================================================
    // Add Page
    //========================================================

    const addPage = () =>

    {

        setDocumentLayout(

            current =>

            ({

                ...current,

                pages:

                    [

                        ...current.pages,

                        createPage()

                    ]

            })

        );

    };


    //========================================================
    // Delete Page
    //
    // A document must always contain at least one page.
    //========================================================

    const deletePage = (pageId) =>

    {

        setDocumentLayout(

            current =>

            {

                if (

                    current.pages.length <= 1

                )

                {

                    return current;

                }


                return {

                    ...current,

                    pages:

                        current.pages.filter(

                            page =>

                                page.id !== pageId

                        )

                };

            }

        );

    };


    //========================================================
    // Add Row
    //========================================================

    const addRow = (

        pageId,

        widths = [100]

    ) =>

    {

        setDocumentLayout(

            current =>

            ({

                ...current,

                pages:

                    current.pages.map(

                        page =>

                        {

                            if (

                                page.id !== pageId

                            )

                            {

                                return page;

                            }


                            return {

                                ...page,

                                rows:

                                    [

                                        ...page.rows,

                                        createRow(

                                            widths

                                        )

                                    ]

                            };

                        }

                    )

            })

        );

    };


    //========================================================
    // Delete Row
    //========================================================

    const deleteRow = (

        pageId,

        rowId

    ) =>

    {

        setDocumentLayout(

            current =>

            ({

                ...current,

                pages:

                    current.pages.map(

                        page =>

                        {

                            if (

                                page.id !== pageId

                            )

                            {

                                return page;

                            }


                            return {

                                ...page,

                                rows:

                                    page.rows.filter(

                                        row =>

                                            row.id !== rowId

                                    )

                            };

                        }

                    )

            })

        );

    };


    //========================================================
    // Move Row
    //
    // Moves the existing row object without recreating its
    // cells or changing any authored content.
    //
    // direction:
    //
    //     -1 = move up
    //      1 = move down
    //========================================================

    const moveRow = (

        pageId,

        rowId,

        direction

    ) =>

    {

        if (

            direction !== -1 &&

            direction !== 1

        )

        {

            return;

        }


        setDocumentLayout(

            current =>

            ({

                ...current,

                pages:

                    current.pages.map(

                        page =>

                        {

                            if (

                                page.id !== pageId

                            )

                            {

                                return page;

                            }


                            const currentIndex =

                                page.rows.findIndex(

                                    row =>

                                        row.id === rowId

                                );


                            if (

                                currentIndex < 0

                            )

                            {

                                return page;

                            }


                            const targetIndex =

                                currentIndex +

                                direction;


                            if (

                                targetIndex < 0 ||

                                targetIndex >= page.rows.length

                            )

                            {

                                return page;

                            }


                            const rows =

                                [

                                    ...page.rows

                                ];


                            const currentRow =

                                rows[currentIndex];


                            rows[currentIndex] =

                                rows[targetIndex];


                            rows[targetIndex] =

                                currentRow;


                            return {

                                ...page,

                                rows:

                                    rows

                            };

                        }

                    )

            })

        );

    };


    //========================================================
    // Change Row Layout
    //
    // Preserve existing cells by position wherever possible.
    //
    // Examples:
    //
    // 100 -> 50 / 50
    //
    // Existing first cell retains its content.
    // Second cell is created empty.
    //
    // 33 / 33 / 33 -> 50 / 50
    //
    // The third cell would be removed.
    //
    // IMPORTANT:
    //
    // Do not silently discard populated cells when reducing
    // the number of columns.
    //========================================================

    const changeRowLayout = (

        pageId,

        rowId,

        widths

    ) =>

    {

        if (

            !Array.isArray(widths) ||

            widths.length === 0

        )

        {

            return;

        }


        setDocumentLayout(

            current =>

            ({

                ...current,

                pages:

                    current.pages.map(

                        page =>

                        {

                            if (

                                page.id !== pageId

                            )

                            {

                                return page;

                            }


                            return {

                                ...page,

                                rows:

                                    page.rows.map(

                                        row =>

                                        {

                                            if (

                                                row.id !== rowId

                                            )

                                            {

                                                return row;

                                            }


                                            //================================
                                            // Prevent Losing Populated Cells
                                            //================================

                                            const removedCells =

                                                row.cells.slice(

                                                    widths.length

                                                );


                                            const wouldLoseContent =

                                                removedCells.some(

                                                    cell =>

                                                        cell.content !== null &&

                                                        cell.content !== undefined

                                                );


                                            if (

                                                wouldLoseContent

                                            )

                                            {

                                                console.warn(

                                                    "Cannot reduce row columns while removed cells contain content."

                                                );


                                                return row;

                                            }


                                            //================================
                                            // Preserve Existing Cells
                                            //================================

                                            const cells =

                                                widths.map(

                                                    (width, index) =>

                                                    {

                                                        const existingCell =

                                                            row.cells[index];


                                                        if (

                                                            existingCell

                                                        )

                                                        {

                                                            return {

                                                                ...existingCell,

                                                                width:

                                                                    width

                                                            };

                                                        }


                                                        return createCell(

                                                            width

                                                        );

                                                    }

                                                );


                                            return {

                                                ...row,

                                                cells:

                                                    cells

                                            };

                                        }

                                    )

                            };

                        }

                    )

            })

        );

    };


    //========================================================
    // Set Cell Content
    //
    // Called when a basket item is dropped.
    //
    // Replaces the cell content with a basket reference.
    //
    // The original source content remains in the basket.
    //========================================================

    const setCellContent = (

        pageId,

        rowId,

        cellId,

        basketId

    ) =>

    {

        if (

            !basketId

        )

        {

            return;

        }


        updateCell(

            pageId,

            rowId,

            cellId,

            cell =>

            ({

                ...cell,

                content:

                {

                    basketId:

                        basketId

                }

            })

        );

    };


    //========================================================
    // Update Cell Content
    //
    // Called by DocumentCell when:
    //
    // - Text is created
    // - Text is edited
    // - Formatting changes
    // - Title changes
    // - Image caption changes
    //
    // Replaces the cell content immutably.
    //
    // A basket reference can coexist with edited text.
    //
    // The original basket item is never modified.
    //========================================================

    const updateCellContent = (

        pageId,

        rowId,

        cellId,

        updatedContent

    ) =>

    {

        updateCell(

            pageId,

            rowId,

            cellId,

            cell =>

            ({

                ...cell,

                content:

                    updatedContent

            })

        );

    };


    //========================================================
    // Clear Cell Content
    //========================================================

    const clearCellContent = (

        pageId,

        rowId,

        cellId

    ) =>

    {

        updateCell(

            pageId,

            rowId,

            cellId,

            cell =>

            ({

                ...cell,

                content:

                    null

            })

        );

    };


    //========================================================
    // Set Page Orientation
    //========================================================

    const setPageOrientation = (

        pageId,

        orientation

    ) =>

    {

        setDocumentLayout(

            current =>

            ({

                ...current,

                pages:

                    current.pages.map(

                        page =>

                        {

                            if (

                                page.id !== pageId

                            )

                            {

                                return page;

                            }


                            return {

                                ...page,

                                orientation:

                                    orientation

                            };

                        }

                    )

            })

        );

    };


    //========================================================
    // Set Document Title
    //========================================================

    const setDocumentTitle = (

        title

    ) =>

    {

        setDocumentLayout(

            current =>

            ({

                ...current,

                title:

                    title

            })

        );

    };


    //========================================================
    // Return
    //========================================================

    return {

        documentLayout,

        setDocumentLayout,

        addPage,

        deletePage,

        addRow,

        deleteRow,

        moveRow,

        changeRowLayout,

        setCellContent,

        updateCellContent,

        clearCellContent,

        setPageOrientation,

        setDocumentTitle

    };

}